import { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineCheck, HiOutlineChevronRight, HiOutlineClock, HiOutlineDocumentSearch, HiPlus, HiUsers } from "react-icons/hi";
import { LuCopy, LuX } from "react-icons/lu";
import { useHistory } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useQuery } from "@tanstack/react-query";
import { useLiveQuery } from "@tanstack/react-db";
import toast from "react-hot-toast";
import { readListState, writeListState, ADMISSION_STATUS, STATUS_REASON_LABELS } from "../../utils";
import API from "../../api";
import { capture } from "../../instrument";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";
import MergeAdmissionsModal from "../../components/MergeAdmissionsModal";
import { BatchRejectModal, CreateAdmissionModal, DuplicateAdmissionModal } from "../../components/admission";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import useSavedViews from "../../hooks/useSavedViews";
import { useHotkeys } from "react-hotkeys-hook";
import useListShortcuts from "../../hooks/useListShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import ShortcutHint from "../../components/shortcuts/ShortcutHint";
import { useAdmissionView } from "../../contexts/AdmissionsContext";
import { useSocket } from "../../contexts/SocketContext";
import { prefetchAdmission } from "../../db/admissions";

const TO_PROCESS_STATUSES = [ADMISSION_STATUS.ERROR, ADMISSION_STATUS.TO_VALIDATE];
const TO_PROCESS_RECENT_DAYS = 7;

const DEFAULT_VIEWS = [
  {
    _id: "TO_PROCESS",
    name: "À traiter",
    filters: [
      { id: "default-to-process-status", field: "status", operator: SEARCH_OPERATOR.IS, values: TO_PROCESS_STATUSES },
      { id: "default-to-process-recent", field: "date", operator: SEARCH_OPERATOR.WITHIN_LAST_DAYS, values: [String(TO_PROCESS_RECENT_DAYS)] },
    ],
  },
  {
    _id: "VALIDATED",
    name: "Validés",
    filters: [{ id: "default-validated", field: "status", operator: SEARCH_OPERATOR.IS, values: [ADMISSION_STATUS.VALIDATED] }],
  },
  { _id: "ALL", name: "Tous", filters: [] },
];

const SOURCE_LABELS = { PATIENT: "Patient", IDE: "IDE" };

const STORAGE_KEY = "admission-list-state";

const BACKFILL_TRIGGER_BUFFER = 20;

const WORKLIST_WARMUP_COUNT = 20;
const WORKLIST_WARMUP_SPACING_MS = 250;

const FLAG_LABELS = {
  PATIENT_NAME_MISSING_IN_PRESCRIPTION: "Nom patient manquant",
  NOT_ELIGIBLE: "Non éligible",
  DUPLICATE: "Doublon",
};

const STATUS_LABELS = {
  DRAFT: "Brouillon",
  TO_VALIDATE: "À valider",
  ON_HOLD: "En attente",
  VALIDATED: "Validé",
  FINALIZING: "Finalisation",
  ERROR: "Erreur",
  QUEUED: "En cours d'enregistrement",
  RECORDED: "Enregistré",
  ARCHIVED: "Archivé",
};

// One tonal recipe for every status: tinted background, same-hue border, same-hue darker
// text. Color is reserved for states that need attention or convey outcome; in-progress
// states share blue, terminal/inactive states stay neutral gray.
const STATUS_CHIP_CLASS = {
  DRAFT: "border-gray-200 bg-gray-50 text-gray-500",
  TO_VALIDATE: "border-orange-200 bg-orange-50 text-orange-700",
  ON_HOLD: "border-blue-200 bg-blue-50 text-blue-700",
  VALIDATED: "border-green-200 bg-green-50 text-green-700",
  FINALIZING: "border-blue-200 bg-blue-50 text-blue-700",
  ERROR: "border-red-200 bg-red-50 text-red-700",
  QUEUED: "border-blue-200 bg-blue-50 text-blue-700",
  RECORDED: "border-green-200 bg-green-50 text-green-700",
  ARCHIVED: "border-gray-200 bg-gray-50 text-gray-500",
};

const formatRelative = (date) => {
  if (!date) return "";
  const diffMs = Date.now() - new Date(date).getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return "à l'instant";
  if (diffMinutes < 60) return `${diffMinutes} min`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "hier";
  if (diffDays < 7) return `${diffDays}j`;
  return new Date(date).toLocaleDateString("fr-FR");
};

const Admissions = () => {
  const history = useHistory();
  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const [search, setSearch] = useState(savedState?.search || "");
  const [presenceCounts, setPresenceCounts] = useState({});
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;
    socket.on("presence-counts", setPresenceCounts);
    return () => socket.off("presence-counts", setPresenceCounts);
  }, [socket]);

  // Debounce prefetch triggers so a fast mouse-wheel scroll (rows flying past the
  // cursor → onMouseEnter firing for each) or a held-down j/k doesn't turn into a
  // backend request storm. Only prefetch when the cursor or selection settles for
  // 180ms — roughly the time to read a row and decide to click.
  const prefetchTimerRef = useRef(null);
  const queueAdmissionPrefetch = (admissionId) => {
    if (!admissionId) return;
    if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
    prefetchTimerRef.current = setTimeout(() => prefetchAdmission(admissionId), 180);
  };
  useEffect(() => () => clearTimeout(prefetchTimerRef.current), []);

  const { data: laboratoriesData } = useQuery({
    queryKey: ["laboratories"],
    queryFn: async () => {
      const { ok, data } = await API.get("/admin/laboratory");
      if (!ok) throw new Error("Failed to get laboratories");
      return data;
    },
    staleTime: 5 * 60_000,
  });
  const laboratories = useMemo(() => laboratoriesData || [], [laboratoriesData]);

  const filterFields = useMemo(
    () => [
      {
        key: "status",
        label: "Statut",
        operators: [SEARCH_OPERATOR.IS],
        values: Object.values(ADMISSION_STATUS),
        valueLabels: STATUS_LABELS,
      },
      {
        key: "source",
        label: "Source",
        operators: [SEARCH_OPERATOR.IS],
        values: Object.keys(SOURCE_LABELS),
        valueLabels: SOURCE_LABELS,
      },
      {
        key: "flags",
        label: "Marqueur",
        operators: [SEARCH_OPERATOR.IS],
        values: Object.keys(FLAG_LABELS),
        valueLabels: FLAG_LABELS,
      },
      {
        key: "urgency",
        label: "Urgence",
        operators: [SEARCH_OPERATOR.IS],
        values: ["URGENT"],
        valueLabels: { URGENT: "Urgent" },
      },
      {
        key: "laboratoryId",
        label: "Laboratoire",
        operators: [SEARCH_OPERATOR.IS],
        values: laboratories.map((laboratory) => laboratory._id),
        valueLabels: Object.fromEntries(laboratories.map((laboratory) => [laboratory._id, laboratory.name])),
      },
      {
        key: "date",
        label: "Date",
        operators: [SEARCH_OPERATOR.WITHIN_LAST_DAYS, SEARCH_OPERATOR.IS],
        type: "date",
        values: [],
        valueLabels: {},
      },
    ],
    [laboratories],
  );

  const { filters: activeFilters, setFilters: setActiveFilters } = useSearchFilters(filterFields, { initialFilters: savedState?.filters });

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    resetSelection();
    clearSelection();
  };

  const onSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
    clearSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "admission",
    defaultViews: DEFAULT_VIEWS,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  const filterSpec = useMemo(() => {
    const spec = {};
    const pick = (field) => activeFilters.find((filter) => filter.field === field);

    const statusFilter = pick("status");
    if (statusFilter?.values?.length) spec.status = statusFilter.values;

    const sourceFilter = pick("source");
    if (sourceFilter?.values?.length) spec.source = sourceFilter.values;

    const flagsFilter = pick("flags");
    if (flagsFilter?.values?.length) spec.flags = flagsFilter.values;

    const urgencyFilter = pick("urgency");
    if (urgencyFilter?.values?.includes("URGENT")) spec.urgent = true;

    const laboratoryFilter = pick("laboratoryId");
    const laboratoryId = laboratoryFilter?.values?.[0];
    if (laboratoryId) spec.laboratoryId = laboratoryId;

    const dateFilter = pick("date");
    if (dateFilter?.values?.[0]) {
      if (dateFilter.operator === SEARCH_OPERATOR.WITHIN_LAST_DAYS) {
        const days = Number(dateFilter.values[0]);
        if (Number.isFinite(days) && days > 0) spec.dateWithinDays = days;
      } else {
        spec.date = dateFilter.values[0];
      }
    }

    if (search.trim()) spec.search = search.trim();

    return spec;
  }, [activeFilters, search]);

  const collection = useAdmissionView(filterSpec);

  const { data: rawAdmissions, status: liveStatus } = useLiveQuery((q) => (collection ? q.from({ admission: collection }) : null), [collection]);

  const isLoading = !collection || (!rawAdmissions && (liveStatus === "loading" || liveStatus === "idle"));

  const admissions = useMemo(() => {
    const copy = [...(rawAdmissions || [])];
    copy.sort((a, b) => {
      const aDate = new Date(a.submittedAt || a.createdAt || 0).getTime();
      const bDate = new Date(b.submittedAt || b.createdAt || 0).getTime();
      return bDate - aDate;
    });
    return copy;
  }, [rawAdmissions]);

  const warmupIds = useMemo(() => admissions.slice(0, WORKLIST_WARMUP_COUNT).map((admission) => admission._id), [admissions]);
  const warmupKey = warmupIds.join();
  useEffect(() => {
    const timers = warmupIds.map((admissionId, index) => setTimeout(() => prefetchAdmission(admissionId), index * WORKLIST_WARMUP_SPACING_MS));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warmupKey]);

  const listRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);
  const [showCreateAdmissionModal, setShowCreateAdmissionModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [showBatchRejectModal, setShowBatchRejectModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const lastSelectedIndexRef = useRef(null);
  const hasSelection = selectedIds.size > 0;

  const virtualizer = useVirtualizer({
    count: admissions.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 52,
    overscan: 8,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  useEffect(() => {
    if (!collection) return;
    if (virtualItems.length === 0) return;
    const lastVisibleIndex = virtualItems[virtualItems.length - 1].index;
    if (lastVisibleIndex >= admissions.length - BACKFILL_TRIGGER_BUFFER) {
      collection.utils.loadOlder();
    }
  }, [virtualItems, admissions.length, collection]);

  const navigateToAdmission = (admission) => {
    const ids = admissions.map((a) => a._id);
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: ids.indexOf(admission._id) });
    history.push({ pathname: `/admissions/${admission._id}`, state: { filteredIds: ids } });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    lastSelectedIndexRef.current = null;
  };

  const onToggleSelection = (admissionId, index, event) => {
    if (event?.shiftKey === true && lastSelectedIndexRef.current !== null) {
      const start = Math.min(lastSelectedIndexRef.current, index);
      const end = Math.max(lastSelectedIndexRef.current, index);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) {
          const id = admissions[i]?._id;
          if (id) next.add(id);
        }
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(admissionId)) next.delete(admissionId);
      else next.add(admissionId);
      return next;
    });
    lastSelectedIndexRef.current = index;
  };

  const onRowClick = (admission, index, event) => {
    const isCtrlOrCmd = event.metaKey === true || event.ctrlKey === true;
    if (isCtrlOrCmd === true || event.shiftKey === true) {
      event.preventDefault();
      onToggleSelection(admission._id, index, event);
      return;
    }
    navigateToAdmission(admission);
  };

  const onSelectAll = () => {
    if (admissions.length === 0) return;
    if (selectedIds.size === admissions.length) {
      clearSelection();
      return;
    }
    setSelectedIds(new Set(admissions.map((admission) => admission._id)));
  };

  const onBatchValidate = async () => {
    setBatchLoading(true);
    const ids = Array.from(selectedIds);
    let successCount = 0;
    let errorCount = 0;
    const incompleteMessages = [];
    const failedIds = [];
    try {
      for (const admissionId of ids) {
        try {
          const { ok } = await API.post(`/admin/admission/${admissionId}/production`);
          if (ok === true) successCount++;
          else {
            errorCount++;
            failedIds.push(admissionId);
          }
        } catch (error) {
          if (error?.code === "UNANSWERED_QUESTIONS") {
            if (successCount > 0) toast.success(`${successCount} dossier${successCount > 1 ? "s" : ""} envoyé${successCount > 1 ? "s" : ""} en production`);
            if (errorCount > 0) toast.error(`${errorCount} dossier${errorCount > 1 ? "s" : ""} en erreur`);
            toast.error("De nouvelles questions ont été ajoutées. Merci de les vérifier avant de valider.");
            clearSelection();
            history.push(`/admissions/${admissionId}`);
            setBatchLoading(false);
            return;
          }
          if (error?.code === "INCOMPLETE_ADMISSION") {
            incompleteMessages.push(error.message || "Le dossier est incomplet et ne peut pas être envoyé en production");
            failedIds.push(admissionId);
            continue;
          }
          capture(error);
          errorCount++;
          failedIds.push(admissionId);
        }
      }
      if (successCount > 0) toast.success(`${successCount} dossier${successCount > 1 ? "s" : ""} envoyé${successCount > 1 ? "s" : ""} en production`);
      if (incompleteMessages.length === 1) toast.error(incompleteMessages[0]);
      if (incompleteMessages.length > 1) toast.error(`${incompleteMessages.length} dossiers incomplets : champs obligatoires manquants`);
      if (errorCount > 0) toast.error(`${errorCount} dossier${errorCount > 1 ? "s" : ""} n'${errorCount > 1 ? "ont" : "a"} pas pu être envoyé${errorCount > 1 ? "s" : ""}`);
      if (failedIds.length > 0) setSelectedIds(new Set(failedIds));
      else clearSelection();
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de l'envoi en production");
    }
    setBatchLoading(false);
  };

  const singleSelectedId = selectedIds.size === 1 ? Array.from(selectedIds)[0] : null;

  const onNavigateDown = () => {
    if (admissions.length === 0) return;
    const next = selectedIndexRef.current >= admissions.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
    queueAdmissionPrefetch(admissions[next]?._id);
  };

  const onNavigateUp = () => {
    if (admissions.length === 0) return;
    const prev = selectedIndexRef.current <= 0 ? admissions.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
    queueAdmissionPrefetch(admissions[prev]?._id);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: onNavigateUp,
      onDown: onNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const admission = admissions[selectedIndexRef.current];
        if (!admission) return;
        if (newTab) window.open(`/admissions/${admission._id}`, "_blank");
        else navigateToAdmission(admission);
      },
    },
    [admissions, virtualizer],
  );

  useHotkeys(SHORTCUTS.SELECT_ALL.keys, onSelectAll, { preventDefault: true, enableOnFormTags: true }, [selectedIds]);
  useHotkeys(SHORTCUTS.CREATE.keys, () => setShowCreateAdmissionModal(true), { preventDefault: true });
  useHotkeys(SHORTCUTS.TOGGLE_SELECT.keys, () => {
    const admission = admissions[selectedIndexRef.current];
    if (admission) onToggleSelection(admission._id, selectedIndexRef.current);
  }, { preventDefault: true }, [admissions]);
  useHotkeys(SHORTCUTS.UNSELECT_ALL.keys, clearSelection, { preventDefault: true, enabled: hasSelection }, [hasSelection]);

  useHotkeys(
    "escape",
    clearSelection,
    {
      enabled: hasSelection === true && showBatchRejectModal === false && showMergeModal === false && showDuplicateModal === false,
      enableOnFormTags: true,
      preventDefault: true,
    },
    [hasSelection, showBatchRejectModal, showMergeModal, showDuplicateModal],
  );

  if (isLoading) return <PageLoadingSpinner />;

  const hasActiveQuery = Boolean(search) || activeFilters.length > 0;

  return (
    <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-6">
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-semibold text-gray-900">Admissions</h1>
            {admissions.length > 0 && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-500">{admissions.length}</span>}
          </div>
          <ViewTabs defaultViews={DEFAULT_VIEWS} savedViews={savedViews} isViewActive={isViewActive} onSelectView={handleSelectView} onDeleteView={handleDeleteView} />
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <div className="hidden items-center gap-1.5 text-[11px] text-gray-400 lg:flex">
            <ShortcutHint keys="up" />
            <ShortcutHint keys="down" />
            <span>naviguer</span>
            <ShortcutHint keys="enter" className="ml-2" />
            <span>ouvrir</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowCreateAdmissionModal(true);
              notifyShortcut("CREATE");
            }}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-600"
          >
            <HiPlus className="h-4 w-4" />
            Nouvelle admission
          </button>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <SearchFilters
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={onFiltersChange}
          search={search}
          onSearchChange={onSearchChange}
          placeholder="Rechercher une admission..."
          saveOptions={saveOptions}
          isLoading={false}
        />

        <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
          {admissions.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const admission = admissions[virtualRow.index];
                const submittedDate = admission.submittedAt || admission.createdAt;
                const submittedLabel = formatRelative(submittedDate);
                const submittedExactLabel = submittedDate ? new Date(submittedDate).toLocaleString("fr-FR") : "";
                const patientName = [admission.patient?.firstName, admission.patient?.lastName].filter(Boolean).join(" ");
                const openQuestions = admission.openSecretaryQuestionCount || 0;
                const statusLabel = STATUS_LABELS[admission.status] || admission.status;
                const statusChipClass = STATUS_CHIP_CLASS[admission.status] || "border-gray-200 bg-gray-50 text-gray-500";
                const statusReasonLabel = [ADMISSION_STATUS.ARCHIVED, ADMISSION_STATUS.RECORDED].includes(admission.status) ? STATUS_REASON_LABELS[admission.statusReason] : null;
                const isUrgent = !!admission.checkInDate && ![ADMISSION_STATUS.RECORDED, ADMISSION_STATUS.ARCHIVED].includes(admission.status);
                const isRowSelected = selectedIds.has(admission._id);
                const concurrentUsers = presenceCounts[admission._id] || 0;

                return (
                  <div
                    key={admission._id}
                    data-active={virtualRow.index === selectedIndex || undefined}
                    onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                    data-selected={isRowSelected || undefined}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                    className="group flex cursor-pointer items-center gap-3 border-b border-gray-100 pr-4 transition-colors hover:bg-gray-50 data-[active=true]:bg-primary-50/50 data-[selected=true]:bg-primary-50 data-[selected=true]:hover:bg-primary-100/70"
                    onClick={(event) => onRowClick(admission, virtualRow.index, event)}
                    onMouseEnter={() => queueAdmissionPrefetch(admission._id)}
                  >
                    <label
                      className={`flex shrink-0 cursor-pointer items-center self-stretch pl-3 pr-1 ${isRowSelected || hasSelection ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isRowSelected}
                        onChange={() => {}}
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleSelection(admission._id, virtualRow.index, event);
                        }}
                        className="size-3.5 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        aria-label={`Sélectionner ${patientName || admission.number}`}
                      />
                    </label>
                    <span className="flex w-44 shrink-0">
                      <span className={`inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-medium ${statusChipClass}`}>
                        {statusLabel}
                        {statusReasonLabel && ` · ${statusReasonLabel}`}
                      </span>
                    </span>
                    <span className="min-w-0 shrink truncate text-sm font-medium text-gray-900">{patientName || <span className="italic text-gray-400">Sans patient</span>}</span>
                    <span className="shrink-0 font-mono text-[11px] uppercase tracking-wide text-gray-400">
                      {admission.number}
                      {admission.silReservationNumber && <span className="ml-1 text-gray-300">· {admission.silReservationNumber}</span>}
                      {admission.silAdmissionNumber && <span className="ml-1 text-gray-300">· {admission.silAdmissionNumber}</span>}
                    </span>
                    <div className="ml-auto flex shrink-0 items-center gap-2 pl-4">
                      {isUrgent && (
                        <span
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700"
                          title="Patient présent — à traiter en priorité"
                        >
                          <HiOutlineClock className="h-3 w-3" />
                          Urgent
                        </span>
                      )}
                      {concurrentUsers > 0 && (
                        <span
                          className="inline-flex items-center gap-1 rounded-md border border-yellow-200 bg-yellow-50 px-1.5 py-0.5 text-[11px] font-medium text-yellow-800"
                          title={`${concurrentUsers} autre${concurrentUsers > 1 ? "s" : ""} personne${concurrentUsers > 1 ? "s" : ""} consulte ce dossier`}
                        >
                          <HiUsers className="h-3 w-3" />
                          {concurrentUsers}
                        </span>
                      )}
                      {openQuestions > 0 && (
                        <span
                          className="inline-flex shrink-0 items-center rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-700"
                          title={`${openQuestions} question${openQuestions > 1 ? "s" : ""} secrétaire à compléter`}
                        >
                          {openQuestions} question{openQuestions > 1 ? "s" : ""} sec.
                        </span>
                      )}
                      <span className="w-12 shrink-0 text-right text-xs tabular-nums text-gray-400" title={submittedExactLabel}>
                        {submittedLabel}
                      </span>
                      <HiOutlineChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-gray-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {admissions.length === 0 && hasActiveQuery && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <HiOutlineDocumentSearch className="mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm">Aucun résultat{search ? ` pour "${search}"` : ""}</p>
              <p className="mt-1 text-xs">Essayez de modifier votre recherche ou vos filtres.</p>
            </div>
          )}

          {admissions.length === 0 && !hasActiveQuery && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <HiOutlineDocumentSearch className="mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm">Aucune admission</p>
              <p className="mt-1 text-xs">Les nouvelles admissions apparaîtront ici.</p>
            </div>
          )}
        </div>
      </div>

      {saveModalOpen && (
        <SaveViewModal
          onClose={() => setSaveModalOpen(false)}
          onConfirm={(name) => {
            setSaveModalOpen(false);
            handleConfirmSaveView(name);
          }}
        />
      )}

      {showCreateAdmissionModal && (
        <CreateAdmissionModal
          laboratories={laboratories}
          defaultLaboratoryId={filterSpec.laboratoryId || ""}
          onClose={() => setShowCreateAdmissionModal(false)}
          onCreated={(newId) => {
            setShowCreateAdmissionModal(false);
            history.push(`/admissions/${newId}`);
          }}
        />
      )}

      {hasSelection === true && (
        <div className="animate-slide-up fixed bottom-6 left-1/2 z-40 -translate-x-1/2">
          <div className="dropdown-panel flex items-center gap-1 px-2 py-1.5">
            <span className="px-2 text-xs tabular-nums text-gray-600">
              <span className="font-medium text-gray-900">{selectedIds.size}</span> dossier{selectedIds.size > 1 ? "s" : ""}
            </span>
            <div className="mx-1 h-5 w-px bg-gray-200" />
            <button
              type="button"
              onClick={onBatchValidate}
              disabled={batchLoading === true}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 py-1.5 pl-2 pr-3 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {batchLoading === true ? (
                <span className="size-3.5 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
              ) : (
                <HiOutlineCheck className="size-4" />
              )}
              Envoyer en production
            </button>
            <button
              type="button"
              onClick={() => setShowBatchRejectModal(true)}
              disabled={batchLoading === true}
              className="inline-flex items-center gap-1.5 rounded-md py-1.5 pl-2 pr-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <LuX className="size-4" />
              Refuser
            </button>
            {selectedIds.size === 1 && (
              <button
                type="button"
                onClick={() => setShowDuplicateModal(true)}
                disabled={batchLoading === true}
                className="inline-flex items-center gap-1.5 rounded-md py-1.5 pl-2 pr-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <LuCopy className="size-4" />
                Dupliquer
              </button>
            )}
            {selectedIds.size >= 2 && (
              <button
                type="button"
                onClick={() => setShowMergeModal(true)}
                disabled={batchLoading === true}
                className="inline-flex items-center gap-1.5 rounded-md py-1.5 pl-2 pr-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <HiUsers className="size-4" />
                Fusionner
              </button>
            )}
            <div className="mx-1 h-5 w-px bg-gray-200" />
            <button
              type="button"
              onClick={clearSelection}
              disabled={batchLoading === true}
              className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              title="Désélectionner tout (Échap)"
            >
              <LuX className="size-4" />
            </button>
          </div>
        </div>
      )}

      {showBatchRejectModal === true && (
        <BatchRejectModal
          selectedAdmissions={selectedIds}
          setSelectedAdmissions={setSelectedIds}
          setShowBatchRejectModal={setShowBatchRejectModal}
          getAdmissions={() => {}}
          setLoading={setBatchLoading}
          loading={batchLoading}
        />
      )}

      {showMergeModal === true && (
        <MergeAdmissionsModal
          selectedAdmissions={selectedIds}
          admissions={admissions}
          setShowMergeModal={setShowMergeModal}
          setSelectedAdmissions={setSelectedIds}
          refreshAdmissions={async (mainAdmissionId) => {
            clearSelection();
            history.push(`/admissions/${mainAdmissionId}`);
          }}
        />
      )}

      {showDuplicateModal === true && singleSelectedId !== null && (
        <DuplicateAdmissionModal
          admissionId={singleSelectedId}
          onClose={() => setShowDuplicateModal(false)}
          getAdmissionDetails={async (newAdmissionId) => {
            history.push(`/admissions/${newAdmissionId}`);
          }}
          setSelectedAdmissions={setSelectedIds}
        />
      )}
    </div>
  );
};

export default Admissions;
